import React, { useState } from 'react';
import { sendContactMessageEmailJS as sendContactMessage } from '../../services/contactServiceEmailJS';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Github, 
  Linkedin, 
  MapPin, 
  Send,
  Heart,
  Coffee
} from 'lucide-react';
import ProtectedEmail from '../ui/ProtectedEmail';
import ProtectedData from '../ui/ProtectedData';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Contact section component
 * Features contact information and social links
 */

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[12]};
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[8]};
  }
`;

const ContactInfo = styled.div`
  background: ${props => props.theme.gradients.cosmic};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing[8]};
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${props => props.theme.patterns.monogram}
    opacity: 0.3;
    pointer-events: none;
  }
`;

const ContactTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes['3xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  margin-bottom: ${props => props.theme.spacing[4]};
  color: white;
`;

const ContactDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.lg};
  opacity: 1;
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  margin-bottom: ${props => props.theme.spacing[8]};
  color: white;
`;

const ContactMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const ContactMethod = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
  padding: ${props => props.theme.spacing[4]};
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${props => props.theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(8px);
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactLabel = styled.h4`
  font-size: ${props => props.theme.typography.sizes.base};
  font-weight: ${props => props.theme.typography.weights.semibold};
  margin-bottom: ${props => props.theme.spacing[1]};
  color: white;
`;

const ContactValue = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  opacity: 1;
  color: white;
`;

const MessageCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing[8]};
  border: 1px solid ${props => props.theme.colors.border};
`;

const MessageTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const MessageDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const MessageForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.base};
  transition: border-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
  }
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.base};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: border-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
  }
`;

const SubmitButton = styled(motion.button)`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  background: ${props => props.theme.gradients.cosmic};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.base};
  font-weight: ${props => props.theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[2]};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.smooth};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const FooterNote = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing[12]};
  padding: ${props => props.theme.spacing[6]};
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  border: 1px solid ${props => props.theme.colors.border};
`;

const FooterText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.base};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[2]};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  
  input[type="checkbox"] {
    margin-top: 4px;
    cursor: pointer;
    width: 18px;
    height: 18px;
    accent-color: ${props => props.theme.colors.primary[600]};
  }
  
  a {
    color: ${props => props.theme.colors.primary[600]};
    text-decoration: underline;
    
    &:hover {
      color: ${props => props.theme.colors.primary[700]};
    }
  }
`;

const Contact: React.FC = () => {
  const { t, language } = useLanguage();
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  // Animation variants removed as they're not being used

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consent) {
      alert(language === 'fr' 
        ? 'Veuillez accepter la politique de confidentialité pour envoyer votre message.'
        : 'Please accept the privacy policy to send your message.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await sendContactMessage({
        ...formData,
        consent
      });
      
      if (response.success) {
        alert(language === 'fr'
          ? '✅ Message envoyé avec succès ! Je vous répondrai dans les plus brefs délais.'
          : '✅ Message sent successfully! I will respond to you as soon as possible.');
        
        // Reset form
        setFormData({ name: '', email: '', subject: '', message: '' });
        setConsent(false);
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      alert(language === 'fr'
        ? `❌ Erreur : ${error.message || 'Impossible d\'envoyer le message. Veuillez me contacter directement à n.benadda.dev@gmail.com'}`
        : `❌ Error: ${error.message || 'Unable to send message. Please contact me directly at n.benadda.dev@gmail.com'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <ContactContainer>
        <ContactInfo>
          <ContactTitle>{t('contact.title')}</ContactTitle>
          <ContactDescription>
            {t('contact.description')}
          </ContactDescription>

          <ContactMethods>
            <ContactMethod
              as="div"
              style={{ cursor: 'pointer' }}
            >
              <ContactIcon>
                <Mail size={24} />
              </ContactIcon>
              <ContactDetails>
                <ContactLabel>{t('contact.email')}</ContactLabel>
                <ProtectedEmail 
                  email="n.benadda.dev@gmail.com"
                  displayName="n.benadda.dev@gmail.com"
                />
              </ContactDetails>
            </ContactMethod>

            <ProtectedData delay={2000}>
              <ContactMethod
                href="https://github.com/adda-neb"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ContactIcon>
                  <Github size={24} />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>{t('contact.github')}</ContactLabel>
                  <ContactValue>github.com/adda-neb</ContactValue>
                </ContactDetails>
              </ContactMethod>
            </ProtectedData>

            <ProtectedData delay={2500}>
              <ContactMethod
                href="https://linkedin.com/in/nadir-benadda-7741b1271/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ContactIcon>
                  <Linkedin size={24} />
                </ContactIcon>
                <ContactDetails>
                  <ContactLabel>{t('contact.linkedin')}</ContactLabel>
                  <ContactValue>linkedin.com/in/nadir-benadda-7741b1271/</ContactValue>
                </ContactDetails>
              </ContactMethod>
            </ProtectedData>

            <ContactMethod
              as="div"
              role="button"
              tabIndex={0}
              aria-label={language === 'fr' ? 'Localisation : Île-de-France, France' : 'Location: Île-de-France, France'}
              style={{ cursor: 'default' }}
            >
              <ContactIcon aria-hidden="true">
                <MapPin size={24} />
              </ContactIcon>
              <ContactDetails>
                <ContactLabel>{t('contact.location')}</ContactLabel>
                <ContactValue>{t('contact.location.value')}</ContactValue>
              </ContactDetails>
            </ContactMethod>
          </ContactMethods>
        </ContactInfo>

        <MessageCard>
          <MessageTitle>{t('contact.message.title')}</MessageTitle>
          <MessageDescription>
            {t('contact.message.description')}
          </MessageDescription>

          <MessageForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">{t('contact.form.name')} *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('contact.form.name.placeholder')}
                required
                aria-required="true"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">{t('contact.form.email')} *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('contact.form.email.placeholder')}
                required
                aria-required="true"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="subject">{t('contact.form.subject')} *</Label>
              <Input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder={t('contact.form.subject.placeholder')}
                required
                aria-required="true"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">{t('contact.form.message')} *</Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={t('contact.form.message.placeholder')}
                required
                aria-required="true"
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  id="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  required
                  aria-required="true"
                />
                <span>
                  {language === 'fr' ? (
                    <>J'accepte que mes données personnelles soient traitées conformément à la{' '}
                    <a href="/politique-confidentialite" target="_blank" rel="noopener noreferrer">
                      politique de confidentialité
                    </a>
                    {' '}*</>
                  ) : (
                    <>I accept that my personal data will be processed in accordance with the{' '}
                    <a href="/politique-confidentialite" target="_blank" rel="noopener noreferrer">
                      privacy policy
                    </a>
                    {' '}*</>
                  )}
                </span>
              </CheckboxLabel>
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={!consent || isSubmitting}
              aria-disabled={!consent || isSubmitting}
              style={{ 
                opacity: (consent && !isSubmitting) ? 1 : 0.6, 
                cursor: (consent && !isSubmitting) ? 'pointer' : 'not-allowed' 
              }}
              whileHover={consent && !isSubmitting ? { y: -2 } : {}}
              whileTap={consent && !isSubmitting ? { scale: 0.98 } : {}}
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Send size={16} aria-hidden="true" />
                  </motion.div>
                  {language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send size={16} aria-hidden="true" />
                  {t('contact.form.submit')}
                </>
              )}
            </SubmitButton>
          </MessageForm>
        </MessageCard>
      </ContactContainer>

      <FooterNote>
        <FooterText>
          {t('contact.footer')} <Heart size={16} color="#ef4444" /> {t('contact.footer.and')} <Coffee size={16} color="#8b5cf6" /> {t('contact.footer.by')}
        </FooterText>
      </FooterNote>
    </motion.div>
  );
};

export default Contact;
